import React, { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Badge, Button, Typography } from 'antd';
import {
  DashboardOutlined, TeamOutlined, BankOutlined, BookOutlined,
  TrophyOutlined, CalendarOutlined, BarChartOutlined,
  BellOutlined, UserOutlined, LogoutOutlined, SettingOutlined,
  SolutionOutlined, SafetyOutlined, ExperimentOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { authApi } from '../services/api';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

// 菜单模块 key → 权限模块名的映射
const MENU_MODULE_MAP: Record<string, string> = {
  '/branches': 'branches',
  '/teachers': 'teachers',
  '/students': 'students',
  '/courses': 'courses',
  '/activities': 'activities',
  '/competitions': 'competitions',
  '/grades': 'grades',
};

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearAuth, refreshToken } = useAuthStore();

  const handleLogout = async () => {
    try {
      if (refreshToken) await authApi.logout(refreshToken);
    } catch {}
    clearAuth();
    window.location.href = '/';
  };

  // 根据角色显示不同菜单
  const getMenuItems = () => {
    const role = user?.role;
    const perms = (user as any)?.permissions || {}; // { branches: { teacher: {view:...}, dean: {view:...} }, ... }
    const items: any[] = [
      { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
    ];

    // 管理端菜单 — 根据权限配置动态显示
    // admin / dean / teacher 及自定义角色均可能看到管理菜单
    const isAdmin = role === 'admin';
    const isStudent = role === 'student';

    if (!isStudent) {
      const checkPerm = (menuKey: string): boolean => {
        if (isAdmin) return true;
        const mod = MENU_MODULE_MAP[menuKey];
        if (!mod || !perms[mod]) return false; // 没有配置则默认不显示（自定义角色需要明确配置）
        const rolePerms = perms[mod][role as string];
        return rolePerms?.view ?? false;
      };

      const mgmtItems = [
        { key: '/branches', icon: <BankOutlined />, label: '学院管理' },
        { key: '/teachers', icon: <SolutionOutlined />, label: '教师管理' },
        { key: '/students', icon: <TeamOutlined />, label: '学生管理' },
        { key: '/courses', icon: <BookOutlined />, label: '课程管理' },
        { key: '/activities', icon: <CalendarOutlined />, label: '活动管理' },
        { key: '/competitions', icon: <TrophyOutlined />, label: '比赛管理' },
        { key: '/grades', icon: <BarChartOutlined />, label: '成绩管理' },
      ].filter((item) => checkPerm(item.key));

      if (mgmtItems.length > 0) {
        items.push(...mgmtItems);
      }
    }

    if (isStudent) {
      items.push(
        { key: '/my-courses', icon: <BookOutlined />, label: '我的课程' },
        { key: '/my-research', icon: <ExperimentOutlined />, label: '我的课题' },
        { key: '/my-grades', icon: <BarChartOutlined />, label: '我的成绩' },
        { key: '/my-activities', icon: <CalendarOutlined />, label: '我的活动' },
        { key: '/my-competitions', icon: <TrophyOutlined />, label: '我的比赛' },
        { key: '/my-profile', icon: <UserOutlined />, label: '我的画像' },
      );
    }

    if (isAdmin) {
      items.push({
        key: '/admin',
        icon: <SettingOutlined />,
        label: '系统管理',
        children: [
          { key: '/admin/users', icon: <UserOutlined />, label: '用户管理' },
          { key: '/admin/permissions', icon: <SafetyOutlined />, label: '权限管理' },
        ],
      });
    }

    return items;
  };

  const userMenuItems = [
    { key: 'profile', icon: <UserOutlined />, label: '个人资料' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="dark"
        width={220}
        style={{ background: '#001529' }}
      >
        <div style={{
          height: 64, display: 'flex', alignItems: 'center',
          justifyContent: 'center', padding: '0 16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <span style={{ fontSize: 20 }}>🔬</span>
          {!collapsed && (
            <Text strong style={{ color: '#fff', marginLeft: 8, fontSize: 14 }}>
              少科院管理
            </Text>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={['/admin']}
          items={getMenuItems()}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0, marginTop: 8 }}
        />
      </Sider>

      <Layout>
        <Header style={{
          background: '#fff', padding: '0 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
        }}>
          <Text strong style={{ fontSize: 16 }}>北京师范大学亚太实验学校 · 少科院管理系统</Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Badge count={0} size="small">
              <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />} />
            </Badge>
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: ({ key }) => {
                  if (key === 'logout') handleLogout();
                  else if (key === 'profile') navigate('/profile');
                },
              }}
              placement="bottomRight"
            >
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar
                  src={user?.avatar}
                  icon={<UserOutlined />}
                  style={{ background: '#16a34a' }}
                />
                <Text>{user?.real_name}</Text>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{ margin: '24px', background: '#f5f5f5', borderRadius: 8 }}>
          <div style={{ padding: 24, minHeight: 'calc(100vh - 112px)', background: '#fff', borderRadius: 8 }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
